import { z } from 'zod';
import {
  AppearanceSchema,
  DisplayModeSchema,
  LinkPolicySchema,
  OriginSchema,
  PLATFORM_DEFAULTS,
} from '../config/index.ts';
import {
  ConversationIdSchema,
  McpServerNameSchema,
  SessionIdSchema,
  TEST_ACCOUNT_SUBJECT_PREFIX,
} from '../ids.ts';

export const PageContextSchema = z.object({
  url: z.string().max(2048).optional(),
  title: z.string().max(512).optional(),
  data: z
    .record(z.string(), z.unknown())
    .refine((value) => JSON.stringify(value).length <= 8192, 'context data is limited to 8 KB')
    .optional(),
});
export type PageContext = z.infer<typeof PageContextSchema>;

export const SessionCreateRequestSchema = z.strictObject({
  user: z.strictObject({
    id: z
      .string()
      .min(1)
      .max(256)
      .refine(
        (id) => !id.startsWith(TEST_ACCOUNT_SUBJECT_PREFIX),
        `user ids starting with "${TEST_ACCOUNT_SUBJECT_PREFIX}" are reserved for test accounts`,
      ),
    name: z.string().max(256).optional(),
    email: z.string().max(320).optional(),
    email_verified: z.boolean().optional(),
    organization_id: z.string().min(1).max(256).optional(),
    plan: z.string().min(1).max(256).optional(),
  }),
  mcp_servers: z
    .array(
      z.strictObject({
        name: McpServerNameSchema,
        authorization_token: z.string().min(1).max(16_384),
        expires_at: z.number().int().positive().optional(),
        refresh_token: z.string().min(1).max(16_384).optional(),
      }),
    )
    .max(8)
    .default([]),
  context: PageContextSchema.optional(),
  expires_in: z
    .number()
    .int()
    .min(PLATFORM_DEFAULTS.sessionLifetimeSecondsMin)
    .max(PLATFORM_DEFAULTS.sessionLifetimeSecondsMax)
    .optional(),
  idle_timeout: z
    .number()
    .int()
    .min(PLATFORM_DEFAULTS.sessionLifetimeSecondsMin)
    .max(PLATFORM_DEFAULTS.sessionLifetimeSecondsMax)
    .optional(),
});
export type SessionCreateRequest = z.input<typeof SessionCreateRequestSchema>;
export type SessionCreateRequestParsed = z.infer<typeof SessionCreateRequestSchema>;

export const SessionCreateResponseSchema = z.object({
  id: SessionIdSchema,
  object: z.literal('session'),
  client_secret: z.string(),
  expires_at: z.number().int().positive(),
  idle_timeout: z.number().int().positive(),
});
export type SessionCreateResponse = z.infer<typeof SessionCreateResponseSchema>;

export const SessionInfoSchema = z.object({
  id: SessionIdSchema,
  object: z.literal('session'),
  expires_at: z.number().int().positive(),
  user: z.object({
    id: z.string(),
    name: z.string().nullable(),
  }),
  settings: z.object({
    assistant_name: z.string(),
    ai_label: z.string().min(1),
    first_message_notice: z.string().min(1),
    link_policy: LinkPolicySchema,
    vendor_origins: z.array(OriginSchema),
    display_modes: z.array(DisplayModeSchema),
    appearance: AppearanceSchema,
    credit_limit_message: z.string().min(1).optional(),
    scheduled_tasks: z.boolean(),
  }),
  mcp_servers: z.array(McpServerNameSchema),
  model: z.object({
    scripted: z.boolean(),
  }),
  models: z
    .object({
      choices: z.array(z.object({ key: z.string(), name: z.string() })).min(1),
      default_key: z.string(),
    })
    .optional(),
  current_conversation_id: ConversationIdSchema.nullable().optional(),
});
export type SessionInfo = z.infer<typeof SessionInfoSchema>;
